import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Brain, CheckCircle, XCircle, ChevronRight, Award, RotateCcw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ArticleQuizEmbed({ articleId, slug }) {
  const { t } = useLanguage();
  const [quizzes, setQuizzes] = useState([]);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(-1);
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!articleId) return;
    axios.get(`${API}/api/quizzes/article/${articleId}`)
      .then(r => setQuizzes(r.data?.quizzes || []))
      .catch(() => {});
  }, [articleId]);

  if (quizzes.length === 0) return null;

  const startQuiz = async (quiz) => {
    try {
      const res = await axios.get(`${API}/api/quizzes/${quiz.id}`);
      setActiveQuiz(res.data);
      setCurrentQ(0);
      setAnswers([]);
      setSelected(-1);
      setResults(null);
    } catch {}
  };

  const selectAnswer = (idx) => {
    if (results) return;
    setSelected(idx);
  };

  const nextQuestion = () => {
    const newAnswers = [...answers, selected];
    setAnswers(newAnswers);
    setSelected(-1);
    if (currentQ + 1 < (activeQuiz?.questions?.length || 0)) {
      setCurrentQ(currentQ + 1);
    } else {
      submitQuiz(newAnswers);
    }
  };

  const submitQuiz = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const lu = JSON.parse(localStorage.getItem('ct_user') || '{}');
      const res = await axios.post(`${API}/api/quizzes/${activeQuiz.id}/submit`, {
        answers: finalAnswers,
        email: lu?.email || ''
      });
      setResults(res.data);
      if (lu?.email) {
        axios.post(`${API}/api/loyalty/earn`, { email: lu.email, action: 'quiz_complete' }).catch(() => {});
      }
    } catch {}
    setSubmitting(false);
  };

  const resetQuiz = () => {
    setActiveQuiz(null);
    setCurrentQ(0);
    setAnswers([]);
    setSelected(-1);
    setResults(null);
  };

  // Quiz listing
  if (!activeQuiz) {
    return (
      <div className="mt-8 mb-4" data-testid="article-quiz-section">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-indigo-100 dark:border-indigo-800">
          <h3 className="font-serif text-xl font-bold text-navy dark:text-white mb-1 flex items-center gap-2">
            <Brain size={20} className="text-indigo-500" /> Testirajte svoje znanje
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Kvizovi vezani uz ovaj \u010dlanak</p>
          <div className="space-y-2">
            {quizzes.map(q => (
              <button key={q.id} onClick={() => startQuiz(q)}
                className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600 transition-all text-left group"
                data-testid={`start-quiz-${q.id}`}>
                <div>
                  <h4 className="font-semibold text-navy dark:text-white text-sm group-hover:text-indigo-600 transition-colors">{q.title}</h4>
                  <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                    <span>{q.questions?.length || 0} pitanja</span>
                    {q.play_count > 0 && <span>{q.play_count} igraca</span>}
                    {q.category && <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{q.category}</span>}
                  </div>
                </div>
                <ChevronRight size={18} className="text-gray-300 group-hover:text-indigo-500 transition-colors" />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Results
  if (results) {
    return (
      <div className="mt-8 mb-4" data-testid="quiz-results">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-center mb-6">
            <Award size={48} className={`mx-auto mb-3 ${results.score >= 70 ? 'text-amber-400' : results.score >= 40 ? 'text-blue-400' : 'text-gray-400'}`} />
            <h3 className="font-serif text-2xl font-bold text-navy dark:text-white">{results.score}%</h3>
            <p className="text-sm text-gray-500">{results.correct}/{results.total} tocnih odgovora</p>
            <p className="text-sm font-medium mt-1 text-gray-600 dark:text-gray-300">
              {results.score >= 80 ? 'Odlicno! Pravi ste strucnjak!' : results.score >= 50 ? 'Dobro! Mozete i bolje!' : 'Pokusajte ponovo!'}
            </p>
          </div>
          <div className="space-y-3 mb-6">
            {results.results?.map((r, i) => (
              <div key={i} className={`p-3 rounded-xl border ${r.is_correct ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800' : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'}`}>
                <div className="flex items-start gap-2">
                  {r.is_correct ? <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" /> : <XCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />}
                  <div>
                    <p className="text-sm font-medium text-navy dark:text-white">{r.question}</p>
                    {r.explanation && <p className="text-xs text-gray-500 mt-1">{r.explanation}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-3">
            <button onClick={resetQuiz} className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 transition-colors" data-testid="quiz-back-btn">
              <RotateCcw size={14} /> Natrag
            </button>
            <button onClick={() => startQuiz(activeQuiz)} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors" data-testid="quiz-retry-btn">
              Pokusaj ponovo
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active quiz question
  const question = activeQuiz.questions?.[currentQ];
  if (!question) return null;

  return (
    <div className="mt-8 mb-4" data-testid="quiz-active">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg font-bold text-navy dark:text-white">{activeQuiz.title}</h3>
          <span className="text-xs font-medium text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
            {currentQ + 1}/{activeQuiz.questions?.length || 0}
          </span>
        </div>
        {/* Progress */}
        <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full mb-6 overflow-hidden">
          <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{ width: `${((currentQ) / (activeQuiz.questions?.length || 1)) * 100}%` }} />
        </div>
        <p className="text-base font-medium text-navy dark:text-white mb-4" data-testid="quiz-question-text">{question.question}</p>
        <div className="space-y-2 mb-6">
          {question.options?.map((opt, i) => (
            <button key={i} onClick={() => selectAnswer(i)}
              className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${selected === i ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300 font-medium' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-100 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'}`}
              data-testid={`quiz-option-${i}`}>
              <span className="mr-2 font-bold text-xs opacity-50">{String.fromCharCode(65 + i)}.</span>
              {opt}
            </button>
          ))}
        </div>
        <div className="flex justify-between items-center">
          <button onClick={resetQuiz} className="text-sm text-gray-400 hover:text-gray-600">Odustani</button>
          <button onClick={nextQuestion} disabled={selected === -1 || submitting}
            className="px-5 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium disabled:opacity-50 hover:bg-indigo-700 transition-colors"
            data-testid="quiz-next-btn">
            {submitting ? '...' : currentQ + 1 < (activeQuiz.questions?.length || 0) ? 'Sljedeće' : 'Završi kviz'}
          </button>
        </div>
      </div>
    </div>
  );
}
